if (!customElements.get('natto-step-article')) {
  // PostHog action names for every article control, including the shared video carousel.
  const trackedActions = [
    ['.lv-reader-next', 'next'],
    ['.lv-reader-back', 'back'],
    ['.lv-reader-offer-shortcut', 'offer_shortcut'],
    ['.lv-reader-buy', 'buy'],
    ['.lv-reader-offer-link', 'offer_link'],
    ['[data-video-direction="-1"]', 'video_previous'],
    ['[data-video-direction="1"]', 'video_next'],
    ['.circulation-videos__toggle', 'video_toggle'],
  ];
  customElements.define(
    'natto-step-article',
    class extends HTMLElement {
      connectedCallback() {
        this.listeners?.abort();
        this.listeners = new AbortController();
        const options = { signal: this.listeners.signal };
        this.steps = [...this.querySelectorAll('.lv-reader-step')];
        if (!this.steps.length) return;
        this.back = this.querySelector('.lv-reader-back');
        this.next = this.querySelector('.lv-reader-next');
        this.buy = this.querySelector('.lv-reader-buy');
        this.nav = this.querySelector('.lv-reader-nav');
        this.progress = this.querySelector('progress');
        this.offerShortcut = this.querySelector('.lv-reader-offer-shortcut');
        this.sourceNote = this.querySelector('.lv-reader-source-note');
        this.offerReturn = null;
        this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.current = this.fromHash();
        this.progress.max = this.steps.length;
        this.back.hidden = false;
        this.querySelector('.lv-reader-progress').hidden = false;
        this.setAttribute('data-enhanced', '');
        // Capture phase runs before Back/Next move, so each click reports the step it was made on.
        this.addEventListener('click', (event) => this.track(event), { ...options, capture: true });
        this.back.addEventListener(
          'click',
          () => {
            const target =
              this.current === this.steps.length - 1
                ? (this.offerReturn ?? this.current - 1)
                : this.current - 1;
            this.offerReturn = null;
            this.moveTo(target);
          },
          options,
        );
        this.next.addEventListener('click', () => this.move(1), options);
        this.offerShortcut?.addEventListener(
          'click',
          () => {
            this.offerReturn = this.current;
            this.moveTo(this.steps.length - 1);
          },
          options,
        );
        const restore = () => {
          this.current = this.fromHash();
          this.render(true);
        };
        window.addEventListener('popstate', restore, options);
        window.addEventListener('hashchange', restore, options);
        this.motionPreference.addEventListener?.(
          'change',
          () => {
            if (this.motionPreference.matches) this.querySelector('[data-animation]')?.pause();
          },
          options,
        );
        this.render(false);
      }

      disconnectedCallback() {
        this.listeners?.abort();
        this.querySelectorAll('video').forEach((video) => video.pause());
      }

      // window.posthog exists only after analytics consent; without it the article is unchanged.
      track(event) {
        const control = event.target.closest('button, a[href]');
        if (!control) return;
        const step = this.steps[this.current];
        const exit = control.matches('a');
        window.posthog?.capture(
          'article_click',
          {
            article_id: 'natto-steps',
            article_action:
              trackedActions.find(([selector]) => control.matches(selector))?.[1] || 'other',
            article_label:
              control.getAttribute('aria-label') ||
              (control.querySelector('strong') ?? control).textContent.trim(),
            article_step: step.dataset.stepName,
            article_step_index: this.current + 1,
            article_step_total: this.steps.length,
          },
          // Links leave the page immediately, so send them without waiting for the batch.
          exit ? { transport: 'sendBeacon' } : undefined,
        );
      }

      fromHash() {
        const match = window.location.hash.match(/^#step-(\d+)$/);
        return match ? Math.max(0, Math.min(this.steps.length - 1, Number(match[1]) - 1)) : 0;
      }

      move(delta) {
        this.moveTo(this.current + delta);
      }

      moveTo(index) {
        const next = Math.max(0, Math.min(this.steps.length - 1, index));
        if (next === this.current) return;
        this.current = next;
        window.history.pushState(window.history.state, '', '#step-' + (next + 1));
        this.render(true);
      }

      render(focus) {
        const total = this.steps.length;
        const last = this.current === total - 1;
        this.steps.forEach((step, index) => {
          step.hidden = index !== this.current;
          if (step.hidden) step.querySelectorAll('video').forEach((video) => video.pause());
        });
        this.progress.value = this.current + 1;
        this.progress.setAttribute('aria-valuetext', `${this.current + 1} / ${total}`);
        this.back.disabled = this.current === 0;
        this.next.hidden = last;
        this.next.textContent =
          this.steps[this.current].dataset.nextLabel || this.dataset.nextLabel;
        this.buy.hidden = !last;
        if (this.offerShortcut) this.offerShortcut.hidden = this.current < 2 || last;
        if (this.sourceNote) this.sourceNote.hidden = !last;
        this.nav.hidden = false;
        const animation = this.steps[this.current].querySelector('[data-animation]');
        if (animation && !this.motionPreference.matches) animation.play().catch(() => {});
        const nextAnimation = this.steps[this.current + 1]?.querySelector('[data-animation]');
        if (nextAnimation) nextAnimation.preload = 'metadata';
        const nextImage = this.steps[this.current + 1]?.querySelector('img');
        if (nextImage) {
          nextImage.loading = 'eager';
          nextImage.fetchPriority = 'low';
          nextImage.decoding = 'async';
        }
        if (focus) {
          const heading = this.steps[this.current].querySelector('.lv-reader-heading');
          heading?.focus({ preventScroll: true });
          window.scrollTo({ top: 0, behavior: 'instant' });
        }
      }
    },
  );
}
